import { ElementType, FC, PropsWithChildren } from 'react';
import { AnimatePresence } from 'framer-motion';
import { cx } from '@emotion/css';
import { MotionDivSlideInFromRight } from '../helpers/animations';
import { BackButton } from './back-button';
import { useNavigation } from '../context/navigation-context-provider';
import { MobileMenuItemProps, MobileMenuListProps, MobileMenuProps, MobileMenuSectionProps } from '../types/navigation.types';
import { getDesignSystemIconWithFallback, getRelativeUrl, isInternalLink } from '../helpers/utils';
import Link from 'next/link';
import styles from '../navigation.module.scss';
import router from 'next/router';

const MobileMenu: MobileMenuProps = ({ children, condition, ariaLabelNav }) => {
  return (
    <AnimatePresence>
      {condition && (
        <MotionDivSlideInFromRight className="absolute inset-0 z-20 flex h-[100vh] w-[100vw] flex-1 flex-col overflow-auto bg-primary-white px-4 pt-4 stihl-text-body-default-reg">
          <nav aria-label={ariaLabelNav}>
            <BackButton />
            {children}
          </nav>
        </MotionDivSlideInFromRight>
      )}
    </AnimatePresence>
  );
};

const MobileMenuList: FC<PropsWithChildren<MobileMenuListProps>> = ({ children, className }) => {
  return <ul className={cx('mx-4 flex flex-col items-start', className)}>{children}</ul>;
};

const MobileMenuItem = <C extends ElementType = 'div'>({
  children,
  item,
  isToplevelItem,
  as,
  classNames,
  preventFocus,
  ...restProps
}: MobileMenuItemProps<C>) => {
  const { setState } = useNavigation();
  const Component = item?.url && item?.url !== '#' ? 'a' : as || 'button';
  const menuOptionName = item?.id?.includes('-quickfilter') ? item.id.replace('-quickfilter', '').toLowerCase() : item?.id?.toLowerCase();
  const activeQuickFilterName = router.query.quickFilterName as string | undefined;
  const isActive = activeQuickFilterName?.toLowerCase() === menuOptionName;
  const href = restProps.href || item?.url;
  const isLink = item?.url && !item?.isFilter && item?.type !== 'Section';

  if (item?.id === 'add-new-filter') {
    return null;
  }

  const commonClassNames = cx(
    classNames,
    'flex w-full items-center justify-between p-4',
    'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline',
    isToplevelItem || item?.type === 'Section' ? 'stihl-text-body-lg-bold' : 'stihl-text-body-default-reg',
    item?.url && styles.mobileNavSectionItem,
    item?.url && isActive && styles.mobileNavSectionItemActive
  );

  const handleClick = () => {
    if (item?.type === 'Section' || isToplevelItem) {
      setState((prevState) => ({ ...prevState, showMobileSubMenu: item.id }));
    } else if (item?.action) {
      item?.action();
      setState((prevState) => ({ ...prevState, showMobileMenu: false }));
    }
  };

  const renderChildren = () => {
    if (children) {
      return children && item?.url ? <span>{children}</span> : children;
    }

    return (
      <>
        <span>{item?.name}</span>
        {item?.type === 'Section' && (
          <img src={getDesignSystemIconWithFallback('icon-chevron-right.svg')} alt="Icon Right" className="h-4 w-4" />
        )}
      </>
    );
  };

  return (
    <li className="flex w-full items-center">
      {!isLink ? (
        <Component tabIndex={preventFocus ? -1 : 0} onClick={() => handleClick()} {...restProps} className={commonClassNames}>
          {renderChildren()}
        </Component>
      ) : isInternalLink(href) ? (
        <Link
          locale={false}
          {...restProps}
          href={getRelativeUrl(href)}
          onClick={() => setTimeout(() => setState((prevState) => ({ ...prevState, showMobileMenu: false })), 400)}
          className={commonClassNames}
        >
          {renderChildren()}
        </Link>
      ) : (
        <a
          {...restProps}
          href={href}
          onClick={() => setTimeout(() => setState((prevState) => ({ ...prevState, showMobileMenu: false })), 400)}
          className={commonClassNames}
        >
          {renderChildren()}
        </a>
      )}
    </li>
  );
};

const MobileMenuSectionHeader: FC<PropsWithChildren<MobileMenuSectionProps>> = ({ children, label, className }) => {
  if (children) {
    return <li className={cx('flex w-full items-center pb-4', className)}>{children}</li>;
  }

  return (
    <li className="pb-4">
      <span className="stihl-text-body-lg-bold">{label}</span>
    </li>
  );
};

MobileMenu.List = MobileMenuList;
MobileMenu.Item = MobileMenuItem;
MobileMenu.SectionHeader = MobileMenuSectionHeader;

export { MobileMenu };
